/**
 * Единый WebSocket клиент для AlgoSys
 * Использует типизированный Envelope и обеспечивает надежное соединение
 */

import { getUserToken } from '../auth/userToken';
import { TIMEOUTS, LIMITS, logError, logWarn } from '../config/constants';
import { AnyBusEvent, EventKind, AnyEventHandler } from '../model/envelope';

// Constants
// Используем прокси через Vite dev server или прямой URL для production.
// Канон: build-time constants (vite define) → без import.meta (Orval/Node-friendly).
const WS_PATH =
  typeof __VITE_WS_URL__ !== 'undefined' && String(__VITE_WS_URL__ || '').trim()
    ? String(__VITE_WS_URL__)
    : '/ws/v2';

const WS_URL = typeof window !== 'undefined' ? WS_PATH : 'ws://localhost:8000/ws/v2';
const RECONNECT_DELAY_BASE = TIMEOUTS.WS_RECONNECT_BASE;
const RECONNECT_DELAY_MAX = TIMEOUTS.WS_RECONNECT_MAX;
const PING_INTERVAL = TIMEOUTS.WS_PING;
const RECONNECT_ATTEMPTS_MAX = LIMITS.MAX_RECONNECT_ATTEMPTS;

type ChannelHandler = (payload: unknown) => void;

export type ChannelMessage = {
  channel: string;
  payload: unknown;
  /** WS sequence (монотонный per-channel), если доступен */
  seq?: number;
  /** Устойчивый курсор "p:o" (Kafka partition + offset) для recovery */
  cursor?: string;
  /** timestamp в ms, если доступен */
  ts?: number;
  /** Epoch процесса WS (ms): меняется при рестарте, помогает детектить seq-reset */
  wsEpoch?: number;
  /** исходный тип envelope (delta/subscribed/recovery/...) */
  kind?: string;
};

type ChannelMetaHandler = (message: ChannelMessage) => void;

/** Параметры, приходящие при событии subscribed (watermark для snapshot boundary) */
export type SubscribedPayload = { seq: number; cursor?: string | undefined; wsEpoch?: number };

interface Subscription {
  channel: string;
  handlers: Set<ChannelHandler>;
  metaHandlers: Set<ChannelMetaHandler>;
  lastSeq: number;
  /** Устойчивый курсор для recovery по Kafka (p:o) */
  lastCursor: string | undefined;
  replayBuffer: unknown[];
  maxBufferSize: number;
  snapshot: boolean;
  /** Enterprise: вызов при получении subscribed (snapshot boundary) */
  onSubscribed?: ((payload: SubscribedPayload) => void) | undefined;
  /** Enterprise: вызов при recovery_failed → UI делает HTTP resync */
  onRecoveryFailed?: (() => void) | undefined;
}

interface WSCommand {
  op: 'sub' | 'unsub' | 'ping' | 'recovery';
  id?: string;
  channel?: string;
  since_seq?: number;
  /** Устойчивый курсор "p:o" для recovery (приоритетнее since_seq) */
  since_cursor?: string;
  snapshot?: boolean;
}

interface WSResponse {
  op: string;
  id?: string;
  channel?: string;
  seq?: number;
  snapshot?: unknown;
  data?: unknown;
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Основной WebSocket клиент
 */
export class BusSocket {
  private ws: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  // Connection state
  private isConnected = false;
  private isReconnecting = false;
  private reconnectAttempts = 0;
  private lastError: string | null = null;

  // Subscriptions
  private subscriptions = new Map<string, Subscription>();

  // Metrics
  private messagesReceived = 0;
  private messagesSent = 0;
  private latency = 0;
  private connectionsCreated = 0;
  private subscriptionsCreated = 0;
  private subscriptionsDestroyed = 0;

  // Event listeners
  private onConnectedCallbacks = new Set<() => void>();
  private onDisconnectedCallbacks = new Set<() => void>();
  private onErrorCallbacks = new Set<(error: string) => void>();

  // Нормализация канала к каноническому виду (в духе серверного парсера)
  private normalizeChannel(channel: string): string {
    try {
      if (typeof channel !== 'string') return channel as unknown as string;
      if (channel.startsWith('ticks:') || channel.startsWith('bars:') || channel.startsWith('ticker:')) {
        const parts = channel.split(':');
        if (parts.length >= 2 && parts[1]) {
          parts[1] = parts[1].toLowerCase();  // Нормализуем к нижнему регистру как на сервере
        }
        return parts.join(':');
      }
      return channel;
    } catch {
      return channel;
    }
  }

  constructor() {
    // Auto-connect on instantiation
    this.connect();
  }

  /**
   * Подключиться к WebSocket
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      let wsUrl = WS_URL.startsWith('ws')
        ? WS_URL
        : `${window.location.origin.replace(/^http/, 'ws')}${WS_URL}`;
      // D11: только per-user JWT (после Google-входа); статического build-time токена нет.
      const wsToken = getUserToken();
      if (wsToken) {
        const sep = wsUrl.includes('?') ? '&' : '?';
        wsUrl = `${wsUrl}${sep}token=${encodeURIComponent(wsToken)}`;
      }

      this.connectionsCreated++;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.handleConnected();
      };

      this.ws.onclose = () => {
        this.handleDisconnected();
      };

      this.ws.onerror = (event) => {
        this.handleError('WebSocket connection error');
        logError('WebSocket error', event);
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event);
      };

    } catch (error) {
      this.handleError(`Failed to connect: ${error}`);
    }
  }

  /**
   * Отключиться от WebSocket
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;
    this.isReconnecting = false;

    // Notify listeners
    this.onDisconnectedCallbacks.forEach(callback => callback());
  }

  /**
   * Подписаться на канал событий
   */
  subscribe(
    channel: string,
    handler: ChannelHandler,
    options: {
      snapshot?: boolean;
      replayBufferSize?: number;
    } = {}
  ): () => void {
    const { snapshot = true, replayBufferSize = 50 } = options;

    const canonical = this.normalizeChannel(channel);

    let subscription = this.subscriptions.get(canonical);

    if (!subscription) {
      this.subscriptionsCreated++;
      subscription = {
        channel: canonical,
        handlers: new Set(),
        metaHandlers: new Set(),
        lastSeq: 0,
        lastCursor: undefined,
        replayBuffer: [],
        maxBufferSize: replayBufferSize,
        snapshot,
      };
      this.subscriptions.set(canonical, subscription);

      // Send subscribe command if connected
      if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
        this.sendCommand({
          op: 'sub',
          channel: canonical,
          snapshot,
        });
      }
    } else {
      // Если кто-то просит snapshot, поднимем флаг для канала (безопасное объединение)
      if (snapshot) subscription.snapshot = true;
    }

    subscription.handlers.add(handler);

    // Send replay buffer to new handler
    subscription.replayBuffer.forEach(data => {
      try {
        handler(data);
      } catch (error) {
        logError('Handler error in replay', error);
      }
    });

    // Return unsubscribe function
    return () => {
      this.unsubscribe(canonical, handler);
    };
  }

  /**
   * Подписка с метаданными (seq/ts/channel).
   * Не ломает существующие subscribe-хендлеры и позволяет enterprise-resume.
   */
  subscribeMeta(
    channel: string,
    handler: ChannelMetaHandler,
    options: {
      snapshot?: boolean;
      replayBufferSize?: number;
      /** Enterprise: watermark при subscribed для snapshot boundary + recovery */
      onSubscribed?: (payload: SubscribedPayload) => void;
      onRecoveryFailed?: () => void;
    } = {}
  ): () => void {
    const { snapshot = true, replayBufferSize = 50, onSubscribed, onRecoveryFailed } = options;
    const canonical = this.normalizeChannel(channel);

    let subscription = this.subscriptions.get(canonical);
    if (!subscription) {
      this.subscriptionsCreated++;
      const newSub: Subscription = {
        channel: canonical,
        handlers: new Set(),
        metaHandlers: new Set(),
        lastSeq: 0,
        lastCursor: undefined,
        replayBuffer: [],
        maxBufferSize: replayBufferSize,
        snapshot,
      };
      if (onSubscribed !== undefined) newSub.onSubscribed = onSubscribed;
      if (onRecoveryFailed !== undefined) newSub.onRecoveryFailed = onRecoveryFailed;
      this.subscriptions.set(canonical, newSub);
      subscription = newSub;

      if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
        this.sendCommand({
          op: 'sub',
          channel: canonical,
          snapshot,
        });
      }
    } else {
      if (snapshot) subscription.snapshot = true;
      if (onSubscribed !== undefined) subscription.onSubscribed = onSubscribed;
      if (onRecoveryFailed !== undefined) subscription.onRecoveryFailed = onRecoveryFailed;
    }

    subscription.metaHandlers.add(handler);
    return () => {
      this.unsubscribeMeta(canonical, handler);
    };
  }

  /**
   * Запрос recovery по каналу начиная с sinceSeq.
   * Сервер вернёт `event_type: recovery` с data.data = payload[].
   */
  recover(channel: string, sinceSeq: number): void {
    const canonical = this.normalizeChannel(channel);
    if (!Number.isFinite(sinceSeq) || sinceSeq <= 0) return;
    this.sendCommand({ op: 'recovery', channel: canonical, since_seq: sinceSeq });
  }

  /**
   * Запрос recovery по устойчивому курсору "p:o" (Kafka partition + offset).
   * Приоритетнее recover(sinceSeq) при наличии cursor.
   */
  recoverByCursor(channel: string, sinceCursor: string): void {
    const canonical = this.normalizeChannel(channel);
    if (!sinceCursor || typeof sinceCursor !== 'string' || !sinceCursor.includes(':')) return;
    this.sendCommand({ op: 'recovery', channel: canonical, since_cursor: sinceCursor });
  }

  /** Последний известный seq по каналу (0 если неизвестен). */
  getLastSeq(channel: string): number {
    const canonical = this.normalizeChannel(channel);
    const sub = this.subscriptions.get(canonical);
    return sub?.lastSeq ?? 0;
  }

  /** Последний известный cursor по каналу (для устойчивого recovery). */
  getLastCursor(channel: string): string | undefined {
    const canonical = this.normalizeChannel(channel);
    const sub = this.subscriptions.get(canonical);
    return sub?.lastCursor;
  }

  /**
   * Отписаться от канала
   */
  unsubscribe(channel: string, handler?: ChannelHandler): void {
    const subscription = this.subscriptions.get(channel);
    if (!subscription) return;

    if (handler) {
      subscription.handlers.delete(handler);
    } else {
      subscription.handlers.clear();
    }

    // If no more handlers, remove subscription
    if (subscription.handlers.size === 0 && subscription.metaHandlers.size === 0) {
      this.subscriptionsDestroyed++;
      this.subscriptions.delete(channel);

      if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
        this.sendCommand({
          op: 'unsub',
          channel,
        });
      }
    }
  }

  private unsubscribeMeta(channel: string, handler?: ChannelMetaHandler): void {
    const subscription = this.subscriptions.get(channel);
    if (!subscription) return;

    if (handler) {
      subscription.metaHandlers.delete(handler);
    } else {
      subscription.metaHandlers.clear();
    }

    if (subscription.handlers.size === 0 && subscription.metaHandlers.size === 0) {
      this.subscriptionsDestroyed++;
      this.subscriptions.delete(channel);

      if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
        this.sendCommand({
          op: 'unsub',
          channel,
        });
      }
    }
  }

  /**
   * Подписаться на события определенного типа
   */
  subscribeByType(
    eventKind: EventKind,
    handler: AnyEventHandler,
    options: { snapshot?: boolean } = {}
  ): () => void {
    // Convert event kind to channel pattern
    const channel = eventKind.replace('.', '/');
    return this.subscribe(
      channel,
      (event: unknown) => {
        const e = event as Partial<AnyBusEvent> | null;
        if (e && typeof e === 'object' && 'kind' in e && e.kind === eventKind) {
          handler(event as AnyBusEvent);
        }
      },
      options,
    );
  }

  /**
   * Добавить слушателя подключения
   */
  onConnected(callback: () => void): () => void {
    this.onConnectedCallbacks.add(callback);
    return () => this.onConnectedCallbacks.delete(callback);
  }

  /**
   * Добавить слушателя отключения
   */
  onDisconnected(callback: () => void): () => void {
    this.onDisconnectedCallbacks.add(callback);
    return () => this.onDisconnectedCallbacks.delete(callback);
  }

  /**
   * Добавить слушателя ошибок
   */
  onError(callback: (error: string) => void): () => void {
    this.onErrorCallbacks.add(callback);
    return () => this.onErrorCallbacks.delete(callback);
  }

  /**
   * Получить текущий статус соединения
   */
  getStatus() {
    const activeSubscriptions = this.subscriptions.size;
    const totalHandlers = Array.from(this.subscriptions.values())
      .reduce((sum, sub) => sum + sub.handlers.size + sub.metaHandlers.size, 0);

    return {
      isConnected: this.isConnected,
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts,
      lastError: this.lastError,
      messagesReceived: this.messagesReceived,
      messagesSent: this.messagesSent,
      latency: this.latency,
      subscriptionCount: activeSubscriptions,
      totalHandlers,
      connectionsCreated: this.connectionsCreated,
      subscriptionsCreated: this.subscriptionsCreated,
      subscriptionsDestroyed: this.subscriptionsDestroyed,
      // Memory leak detection: if destroyed < created significantly, there might be a leak
      subscriptionLeakDetected: this.subscriptionsCreated - this.subscriptionsDestroyed > activeSubscriptions * 2,
    };
  }

  /**
   * Обработчик успешного подключения
   */
  private handleConnected(): void {
    this.isConnected = true;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.lastError = null;

    // Start ping interval
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendCommand({ op: 'ping', id: Date.now().toString() });
      }
    }, PING_INTERVAL);

    // (Пере)подписка на активные каналы.
    // Важно: если подписка была создана ДО открытия сокета, первичный `sub` не отправлялся.
    // Ранее мы пытались делать только `recovery`, но сервер может молча игнорировать unsupported op,
    // из-за чего UI остаётся без реальных подписок (subscribers_count=0) и не получает бары.
    this.subscriptions.forEach((subscription) => {
      // Проверяем, есть ли активные подписчики на этот канал
      const subscriberCount = (subscription.handlers?.size || 0) + (subscription.metaHandlers?.size || 0);
      if (subscriberCount === 0) {
        return; // Пропускаем каналы без подписчиков
      }

      // Всегда отправляем `sub`, чтобы сервер зарегистрировал канал.
      this.sendCommand({
        op: 'sub',
        channel: subscription.channel,
        snapshot: subscription.snapshot,
      });
    });

    // Notify listeners
    this.onConnectedCallbacks.forEach(callback => callback());
  }

  /**
   * Обработчик отключения
   */
  private handleDisconnected(): void {
    this.isConnected = false;

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    // Notify listeners
    this.onDisconnectedCallbacks.forEach(callback => callback());

    // Attempt reconnect
    if (this.reconnectAttempts < RECONNECT_ATTEMPTS_MAX) {
      this.isReconnecting = true;
      this.reconnectAttempts++;

      // Clear any existing reconnect timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      const delay = Math.min(
        RECONNECT_DELAY_BASE * Math.pow(2, this.reconnectAttempts - 1),
        RECONNECT_DELAY_MAX
      );

      this.reconnectTimeout = setTimeout(() => {
        this.connect();
      }, delay);
    }
  }

  /**
   * Обработчик ошибок
   */
  private handleError(error: string): void {
    this.lastError = error;
    this.onErrorCallbacks.forEach(callback => callback(error));
  }

  /**
   * Обработчик входящих сообщений
   */
  private async handleMessage(event: MessageEvent): Promise<void> {
    this.messagesReceived++;

    try {
      let data: WSResponse;

      if (event.data instanceof Blob) {
        // Binary message - for future use
        const buffer = await event.data.arrayBuffer();
        const text = new TextDecoder().decode(buffer);
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          logError('Failed to parse binary WebSocket message', parseError);
          return; // Skip processing invalid messages
        }
      } else {
        // Text message
        try {
          data = JSON.parse(event.data as string);
        } catch (parseError) {
          logError('Failed to parse text WebSocket message', parseError);
          return; // Skip processing invalid messages
        }
      }

      this.processResponse(data);
    } catch (error) {
      logError('Failed to process WebSocket message', error);
    }
  }

  /**
   * Обработать ответ сервера
   */
  private processResponse(response: unknown): void {
    if (!response || typeof response !== 'object') {
      return;
    }
    const r = response as Record<string, unknown>;
    // Унификация формата: сервер может слать op/type/event_type
    const kind = (r.op || r.type || r.event_type) as string | undefined;
    const wsEpoch =
      typeof r.ws_epoch === 'number'
        ? (r.ws_epoch as number)
        : typeof (r as { wsEpoch?: unknown }).wsEpoch === 'number'
          ? ((r as { wsEpoch?: unknown }).wsEpoch as number)
          : undefined;

    switch (kind) {
      case 'pong': {
        const data = r.data as Record<string, unknown> | null | undefined;
        const reqId = (r.id as string | undefined) ?? (data?.request_id as string | undefined);
        if (reqId) {
          const pingTime = Date.now() - parseInt(reqId, 10);
          this.latency = pingTime;
        }
        break;
      }

      case 'subscribed':
      case 'sub_ack': {
        const ch = r.channel as string | undefined;
        const seq = r.seq as number | undefined;
        const cursor = typeof r.cursor === 'string' ? (r.cursor as string) : undefined;
        const ts = (typeof r.ts === 'number' ? (r.ts as number) : typeof r.timestamp === 'number' ? (r.timestamp as number) : undefined);
        if (ch) {
          const subscription = this.subscriptions.get(ch);
          if (subscription) {
            if (seq !== undefined) subscription.lastSeq = seq;
            if (cursor) subscription.lastCursor = cursor;
            if (subscription.onSubscribed !== undefined && seq !== undefined) {
              subscription.onSubscribed({
                seq,
                cursor: cursor ?? undefined,
                ...(wsEpoch !== undefined ? { wsEpoch } : {}),
              });
            }
          }
        }
        // Опциональный снапшот может прийти в data.snapshot или snapshot
        const data = r.data as Record<string, unknown> | null | undefined;
        const snapshot = data?.snapshot ?? r.snapshot;
        if (ch && snapshot) {
          this.distributeRaw(ch, snapshot, {
            kind: String(kind),
            ...(seq !== undefined ? { seq } : {}),
            ...(cursor !== undefined ? { cursor } : {}),
            ...(ts !== undefined ? { ts } : {}),
            ...(wsEpoch !== undefined ? { wsEpoch } : {}),
          });
        }
        break;
      }

      case 'recovery': {
        const ch = r.channel as string | undefined;
        const data = r.data as Record<string, unknown> | null | undefined;
        const dataArr = data?.data as unknown[] | undefined;
        if (ch && Array.isArray(dataArr)) {
          let lastCursor: string | undefined;
          for (const item of dataArr) {
            // Сервер отдаёт элементы recovery как {seq, cursor, ts, payload}
            if (item && typeof item === 'object') {
              const rec = item as Record<string, unknown>;
              const payload = rec.payload;
              const seq = typeof rec.seq === 'number' ? (rec.seq as number) : undefined;
              const cursor = typeof rec.cursor === 'string' ? (rec.cursor as string) : undefined;
              if (cursor) lastCursor = cursor;
              const ts = typeof rec.ts === 'number' ? (rec.ts as number) : undefined;
              if (payload !== undefined) {
                this.distributeRaw(ch, payload, {
                  kind: 'recovery_item',
                  ...(seq !== undefined ? { seq } : {}),
                  ...(cursor !== undefined ? { cursor } : {}),
                  ...(ts !== undefined ? { ts } : {}),
                  ...(wsEpoch !== undefined ? { wsEpoch } : {}),
                });
                continue;
              }
            }
            // fallback: если формат не тот, доставляем как есть
            this.distributeRaw(ch, item, { kind: 'recovery_item', ...(wsEpoch !== undefined ? { wsEpoch } : {}) });
          }
          const subscription = this.subscriptions.get(ch);
          if (subscription) {
            if (typeof r.seq === 'number') subscription.lastSeq = r.seq;
            if (lastCursor) subscription.lastCursor = lastCursor;
          }
        }
        break;
      }

      case 'delta':
      case 'event': {
        const ch = r.channel as string | undefined;
        const payload = r.payload ?? r.data;
        const seq = typeof r.seq === 'number' ? (r.seq as number) : undefined;
        const cursor = typeof r.cursor === 'string' ? (r.cursor as string) : undefined;
        const ts = (typeof r.ts === 'number' ? (r.ts as number) : typeof r.timestamp === 'number' ? (r.timestamp as number) : undefined);
        if (ch && payload) {
          const subscription = this.subscriptions.get(ch);
          if (subscription && seq !== undefined) {
            subscription.lastSeq = seq;
          }
          if (subscription && cursor) {
            subscription.lastCursor = cursor;
          }
          this.distributeRaw(ch, payload, {
            kind: String(kind),
            ...(seq !== undefined ? { seq } : {}),
            ...(cursor !== undefined ? { cursor } : {}),
            ...(ts !== undefined ? { ts } : {}),
            ...(wsEpoch !== undefined ? { wsEpoch } : {}),
          });
          break;
        }
        if (r.data) {
          this.distributeEvent(r.data as AnyBusEvent);
        }
        break;
      }

      case 'welcome': {
        // no-op
        break;
      }

      case 'error': {
        const error = r.error as Record<string, unknown> | null | undefined;
        const errorCode = (error?.code ?? error?.type) as string | undefined;
        const ch = r.channel as string | undefined;
        this.handleError((error?.message as string | undefined) || 'WebSocket error');

        if (ch && errorCode === 'recovery_failed') {
          const subscription = this.subscriptions.get(ch) ?? (ch ? this.subscriptions.get(this.normalizeChannel(ch)) : undefined);
          subscription?.onRecoveryFailed?.();
        }

        // Дополнительная обработка ошибок подписки
        if (errorCode === 'invalid_channel') {
          logError(`Invalid channel subscription: ${ch ?? ''}`);
        } else if (errorCode === 'subscription_failed') {
          logError(`Subscription failed for channel: ${ch ?? ''}`);
        } else if (errorCode === 'unsupported_operation' && ch) {
          // Recovery не поддерживается - делаем обычную подписку
          const subscription = this.subscriptions.get(ch);
          if (subscription && (subscription.handlers?.size || 0) > 0) {
            this.sendCommand({
              op: 'sub',
              channel: ch,
              snapshot: true,
            });
          }
        }

        // Логируем все ошибки для отладки
        logError('WebSocket error details', {
          code: error?.code,
          message: error?.message,
          channel: r.channel,
          seq: r.seq
        });

        break;
      }

      default: {
        // неизвестный тип — игнорируем, но не падаем
        break;
      }
    }
  }

  // Доставка payload по реальному каналу
  private distributeRaw(
    channel: string,
    payload: unknown,
    meta?: { kind?: string; seq?: number; cursor?: string; ts?: number; wsEpoch?: number }
  ): void {
    const subscription = this.subscriptions.get(channel) || this.subscriptions.get(this.normalizeChannel(channel));
    if (!subscription) {
      logWarn('No subscription found for channel', channel);
      return;
    }

    subscription.replayBuffer.push(payload);
    if (subscription.replayBuffer.length > subscription.maxBufferSize) {
      subscription.replayBuffer.shift();
    }

    const msg: ChannelMessage = {
      channel: subscription.channel,
      payload,
      ...(meta?.seq !== undefined ? { seq: meta.seq } : {}),
      ...(meta?.cursor !== undefined ? { cursor: meta.cursor } : {}),
      ...(meta?.ts !== undefined ? { ts: meta.ts } : {}),
      ...(meta?.wsEpoch !== undefined ? { wsEpoch: meta.wsEpoch } : {}),
      ...(meta?.kind ? { kind: meta.kind } : {}),
    };

    subscription.handlers.forEach(handler => {
      try {
        handler(payload);
      } catch (error) {
        logError('Event handler error', error);
      }
    });

    subscription.metaHandlers.forEach(handler => {
      try {
        handler(msg);
      } catch (error) {
        logError('Meta handler error', error);
      }
    });
  }

  /**
   * Распределить событие по подписчикам
   */
  private distributeEvent(event: AnyBusEvent): void {
    // Find matching subscriptions
    const channel = event.kind.replace('.', '/');
    const subscription = this.subscriptions.get(channel);

    if (subscription) {
      // Add to replay buffer (no sequence checking for now)
      subscription.replayBuffer.push(event);
      if (subscription.replayBuffer.length > subscription.maxBufferSize) {
        subscription.replayBuffer.shift();
      }

      // Call handlers
      subscription.handlers.forEach(handler => {
        try {
          handler(event);
        } catch (error) {
          logError('Event handler error', error);
        }
      });
    }
  }

  /**
   * Отправить команду на сервер
   */
  private sendCommand(command: WSCommand): void {
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logWarn('Cannot send command: WebSocket not connected');
      return;
    }

    try {
      this.ws.send(JSON.stringify(command));
      this.messagesSent++;
    } catch (error) {
      logError('Failed to send WebSocket command', error);
    }
  }
}

// Singleton instance
let busSocketInstance: BusSocket | null = null;

/**
 * Получить экземпляр WebSocket клиента
 */
export const getBusSocket = (): BusSocket => {
  if (!busSocketInstance) {
    busSocketInstance = new BusSocket();
  }
  return busSocketInstance;
};

/**
 * Сбросить экземпляр (для тестирования)
 */
export const resetBusSocket = (): void => {
  if (busSocketInstance) {
    busSocketInstance.disconnect();
    busSocketInstance = null;
  }
};
